/**
 * BulkLicenseDialog Component
 *
 * Modal dialog for bulk license assignment to multiple users.
 * Allows adding and removing licenses for selected users in a single operation.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';
import { Dismiss24Regular, Add20Regular, Subtract20Regular } from '@fluentui/react-icons';
import { UserApiService } from '../../../services/userApi';
import type { TenantUser, LicenseInfo, BulkLicenseResult } from '../../../types/user';
import { Alert } from '../../common/Alert';

interface BulkLicenseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  selectedUsers: TenantUser[];
}

const useStyles = makeStyles({
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  userList: {
    maxHeight: '150px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
  },
  userName: {
    display: 'block',
    marginBottom: tokens.spacingVerticalXS,
  },
  licenseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  licenseItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  licenseInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  selectedLicense: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  resultsSection: {
    marginTop: tokens.spacingVerticalL,
  },
  resultItem: {
    padding: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXXS,
  },
  successResult: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorResult: {
    color: tokens.colorPaletteRedForeground1,
  },
});

export const BulkLicenseDialog: React.FC<BulkLicenseDialogProps> = ({
  isOpen,
  onClose,
  tenantId,
  selectedUsers,
}) => {
  const styles = useStyles();

  const [licenses, setLicenses] = useState<LicenseInfo[]>([]);
  const [licensesToAdd, setLicensesToAdd] = useState<string[]>([]);
  const [licensesToRemove, setLicensesToRemove] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BulkLicenseResult | null>(null);

  // Load available licenses on mount
  useEffect(() => {
    if (isOpen) {
      loadLicenses();
    }
  }, [isOpen, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLicenses = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const availableLicenses = await UserApiService.getAvailableLicenses(tenantId);
      setLicenses(availableLicenses);
    } catch (err) {
      setError('Failed to load licenses');
      // eslint-disable-next-line no-console
      console.error('Error loading licenses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAddLicense = (skuId: string) => {
    setLicensesToAdd(prev =>
      prev.includes(skuId)
        ? prev.filter(id => id !== skuId)
        : [...prev, skuId]
    );
  };

  const toggleRemoveLicense = (skuId: string) => {
    setLicensesToRemove(prev =>
      prev.includes(skuId)
        ? prev.filter(id => id !== skuId)
        : [...prev, skuId]
    );
  };

  const handleApply = async () => {
    if (licensesToAdd.length === 0 && licensesToRemove.length === 0) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const userIds = selectedUsers.map(user => user.id);
      const bulkResult = await UserApiService.bulkAssignLicenses(tenantId, {
        user_ids: userIds,
        add_licenses: licensesToAdd,
        remove_licenses: licensesToRemove,
      });

      setResult(bulkResult);
    } catch (err) {
      setError('Bulk operation failed');
      // eslint-disable-next-line no-console
      console.error('Error in bulk license assignment:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset state when closing
    setLicensesToAdd([]);
    setLicensesToRemove([]);
    setResult(null);
    setError(null);
    onClose();
  };

  const hasChanges = licensesToAdd.length > 0 || licensesToRemove.length > 0;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_ev, data) => {
        if (!data.open) {
          handleClose();
        }
      }}
    >
      <DialogSurface data-testid="bulk-license-dialog" style={{ maxWidth: '600px' }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={handleClose}
                disabled={isSubmitting}
              />
            }
          >
            Bulk License Assignment
          </DialogTitle>

          <DialogContent>
            {/* Selected Users Section */}
            <div className={styles.section}>
              <Text weight="semibold" block style={{ marginBottom: tokens.spacingVerticalS }}>
                {selectedUsers.length} users selected
              </Text>
              <div className={styles.userList}>
                {selectedUsers.map(user => (
                  <Text key={user.id} className={styles.userName}>
                    {user.display_name}
                  </Text>
                ))}
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div style={{ marginBottom: tokens.spacingVerticalM }}>
                <Alert intent="error">{error}</Alert>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXL }}>
                <Spinner label="Loading licenses..." />
              </div>
            )}

            {/* Available Licenses */}
            {!isLoading && !result && (
              <div className={styles.section}>
                <Text weight="semibold" block style={{ marginBottom: tokens.spacingVerticalS }}>
                  Available Licenses
                </Text>
                <div className={styles.licenseList}>
                  {licenses.map(license => (
                    <div key={license.sku_id} className={styles.licenseItem}>
                      <div className={styles.licenseInfo}>
                        <Text weight="semibold">{license.display_name}</Text>
                        <Text size={200}>
                          {license.available} available / {license.enabled} total
                        </Text>
                      </div>
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                        <Button
                          appearance={licensesToAdd.includes(license.sku_id) ? 'primary' : 'secondary'}
                          icon={<Add20Regular />}
                          onClick={() => toggleAddLicense(license.sku_id)}
                          disabled={isSubmitting}
                          aria-label={`Add ${license.display_name}`}
                        >
                          Add
                        </Button>
                        <Button
                          appearance={licensesToRemove.includes(license.sku_id) ? 'primary' : 'secondary'}
                          icon={<Subtract20Regular />}
                          onClick={() => toggleRemoveLicense(license.sku_id)}
                          disabled={isSubmitting}
                          aria-label={`Remove ${license.display_name}`}
                        >
                          Remove
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Selected Licenses Summary */}
            {!result && hasChanges && (
              <div className={styles.section}>
                {licensesToAdd.length > 0 && (
                  <div style={{ marginBottom: tokens.spacingVerticalS }}>
                    <Text weight="semibold" block>Licenses to add:</Text>
                    {licensesToAdd.map(skuId => {
                      const license = licenses.find(l => l.sku_id === skuId);
                      return (
                        <Text key={skuId} size={200} block>
                          • {license?.display_name || skuId}
                        </Text>
                      );
                    })}
                  </div>
                )}
                {licensesToRemove.length > 0 && (
                  <div>
                    <Text weight="semibold" block>Licenses to remove:</Text>
                    {licensesToRemove.map(skuId => {
                      const license = licenses.find(l => l.sku_id === skuId);
                      return (
                        <Text key={skuId} size={200} block>
                          • {license?.display_name || skuId}
                        </Text>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Results Section */}
            {result && (
              <div className={styles.resultsSection}>
                <Text weight="semibold" block style={{ marginBottom: tokens.spacingVerticalS }}>
                  Operation Complete
                </Text>
                <Text block style={{ marginBottom: tokens.spacingVerticalM }}>
                  {result.successful} successful, {result.failed} failed
                </Text>

                {/* Show detailed results */}
                {result.results.map((userResult) => {
                  const user = selectedUsers.find(u => u.id === userResult.user_id);
                  return (
                    <div
                      key={userResult.user_id}
                      className={`${styles.resultItem} ${
                        userResult.success ? styles.successResult : styles.errorResult
                      }`}
                    >
                      <Text block>
                        {user?.display_name || userResult.user_id}:{' '}
                        {userResult.success ? '✓ Success' : `✗ ${userResult.error_message}`}
                      </Text>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>

          <DialogActions>
            {!result ? (
              <>
                <Button
                  appearance="secondary"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <AsyncButton
                  appearance="primary"
                  onClick={handleApply}
                  disabled={!hasChanges || isSubmitting}
                  loading={isSubmitting}
                  loadingText="Applying..."
                >
                  Apply
                </AsyncButton>
              </>
            ) : (
              <Button appearance="primary" onClick={handleClose}>
                Close
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
